// Metaobject Search Client for Shopify
// Place this file in assets/metaobject-search.js

class MetaobjectSearchClient {
  constructor (config) {
    this.workerEndpoint = config.workerEndpoint
    this.searchInput = config.searchInput || 'input[name="q"]'
    this.resultsContainer = config.resultsContainer || '#metaobject-results'
    this.loadingContainer = config.loadingContainer || '#metaobject-loading-inline'
    this.debounceDelay = config.debounceDelay || 300
    this.minQueryLength = config.minQueryLength || 2
    this.metaobjectTypes = config.metaobjectTypes || []

    this.init()
  }

  init () {
    // Get search query from URL
    const urlParams = new URLSearchParams(window.location.search)
    const searchQuery = urlParams.get('q')

    if (searchQuery && searchQuery.length >= this.minQueryLength) {
      // Wait for page to load before searching
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.performSearch(searchQuery)
        })
      } else {
        // Small delay to ensure DOM is ready
        setTimeout(() => this.performSearch(searchQuery), 100)
      }
    }
  }

  async performSearch (query, cursor = null) {
    const container = document.querySelector(this.resultsContainer)
    const loadingContainer = document.querySelector(this.loadingContainer)

    if (!container) return

    // Show loading state inline in content column (only for initial search, not pagination)
    if (!cursor) {
      if (loadingContainer) {
        console.log('Showing metaobject loading state')
        // Only show if not already visible (to avoid flicker)
        if (loadingContainer.style.display !== 'block') {
          loadingContainer.style.display = 'block'
        }
      } else {
        console.log('Loading container not found')
      }
    }

    // Determine environment based on current domain
    let environment = 'production'
    const hostname = window.location.hostname

    if (hostname.includes('staging') || hostname.includes('singular-sleep-staging')) {
      environment = 'staging'
    } else if (hostname.endsWith('.myshopify.com') && !hostname.includes('sleepdoctor')) {
      // Assume non-production myshopify domains are staging
      environment = 'staging'
    }

    console.log('Metaobject search environment detection:', {
      hostname,
      detectedEnvironment: environment,
      workerEndpoint: this.workerEndpoint
    })

    try {
      const response = await fetch(this.workerEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Environment': environment
        },
        body: JSON.stringify({
          query,
          limit: 250,
          cursor,
          types: this.metaobjectTypes
        })
      })

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }

      const data = await response.json()

      if (cursor) {
        // Append results for pagination
        this.appendResults(data)
      } else {
        // Display initial results
        this.displayResults(data, query)
      }
    } catch (error) {
      console.error('Metaobject search error:', error)
      this.displayError(error.message)
    } finally {
      const loadingContainer = document.querySelector(this.loadingContainer)
      if (loadingContainer) {
        loadingContainer.style.display = 'none'
      }
    }
  }

  displayResults (data, query) {
    const container = document.querySelector(this.resultsContainer)
    if (!container) return

    // Clear previous results
    container.innerHTML = ''

    // Hide loading state
    const loadingContainer = document.querySelector(this.loadingContainer)
    if (loadingContainer) {
      loadingContainer.style.display = 'none'
    }

    if (data.items.length === 0) {
      // No metaobject results found
      // Find the content results container to show "no content results" message
      const contentResultsSection = document.querySelector('#search-content-results .search-results-items')
      if (contentResultsSection) {
        // Check if there are any existing content results
        const existingResults = contentResultsSection.querySelectorAll('.search-result-item')
        if (existingResults.length === 0) {
          // Add "no content results" message if no existing results
          const noResultsDiv = document.createElement('div')
          noResultsDiv.className = 'search-no-content-results'
          noResultsDiv.innerHTML = '<p>No content results found</p>'
          noResultsDiv.style.padding = '2rem 0'
          noResultsDiv.style.textAlign = 'center'
          noResultsDiv.style.color = 'rgba(var(--color-foreground), 0.75)'
          contentResultsSection.appendChild(noResultsDiv)
        }
      }

      // Update tab counts even with no results
      if (window.searchTabManager) {
        window.searchTabManager.updateCounts()
      }

      return
    }

    // Find the content results container (left side on desktop, tab on mobile)
    const contentResultsSection = document.querySelector('#search-content-results .search-results-items')
    if (!contentResultsSection) {
      console.warn('Content results container not found')
      return
    }

    // Remove loading container if it exists
    const inlineLoading = contentResultsSection.querySelector('#metaobject-loading-inline')
    if (inlineLoading) {
      inlineLoading.style.display = 'none'
    }

    // Get the current result count in content section
    const existingResultsCount = this.getExistingContentResultsCount()

    // Create metaobject results and append to content section
    data.items.forEach((item, index) => {
      const resultIndex = existingResultsCount + index + 1
      const listItem = this.createListItem(item, resultIndex)
      contentResultsSection.appendChild(listItem)
    })

    // Update the total count
    this.updateResultCounts(existingResultsCount + data.items.length)

    // Store the last index for pagination
    this.lastResultIndex = existingResultsCount + data.items.length

    // Update tab counts if tab manager exists
    if (window.searchTabManager) {
      window.searchTabManager.updateCounts()
    }

    // Add load more button if needed
    if (data.pageInfo.hasNextPage) {
      const paginationContainer = document.querySelector('.pagination') || this.createPaginationContainer()

      // Remove existing load more button if present
      const existingLoadMore = paginationContainer.querySelector('.metaobject-load-more')
      if (existingLoadMore) {
        existingLoadMore.remove()
      }

      const loadMoreBtn = document.createElement('button')
      loadMoreBtn.className = 'button button--primary metaobject-load-more'
      loadMoreBtn.textContent = 'Load More Results'
      loadMoreBtn.onclick = () => {
        this.performSearch(query, data.pageInfo.endCursor)
        loadMoreBtn.disabled = true
        loadMoreBtn.textContent = 'Loading...'
      }

      paginationContainer.appendChild(loadMoreBtn)
    }
  }

  appendResults (data) {
    // Find the content results container
    const contentResultsSection = document.querySelector('#search-content-results .search-results-items')
    if (!contentResultsSection) return

    // Continue numbering from where we left off
    const startIndex = this.lastResultIndex || this.getExistingContentResultsCount()

    data.items.forEach((item, index) => {
      const resultIndex = startIndex + index + 1
      const listItem = this.createListItem(item, resultIndex)
      contentResultsSection.appendChild(listItem)
    })

    // Update the last index and counts
    this.lastResultIndex = startIndex + data.items.length
    this.updateResultCounts(this.lastResultIndex)

    // Update tab counts if tab manager exists
    if (window.searchTabManager) {
      window.searchTabManager.updateCounts()
    }

    // Update or remove load more button
    const paginationContainer = document.querySelector('.pagination')
    const loadMoreBtn = paginationContainer?.querySelector('.metaobject-load-more')

    if (loadMoreBtn) {
      if (data.pageInfo.hasNextPage) {
        loadMoreBtn.disabled = false
        loadMoreBtn.textContent = 'Load More Results'
        loadMoreBtn.onclick = () => {
          const urlParams = new URLSearchParams(window.location.search)
          const query = urlParams.get('q')
          this.performSearch(query, data.pageInfo.endCursor)
        }
      } else {
        loadMoreBtn.remove()
      }
    }
  }

  createListItem (item, index) {
    const resultItem = document.createElement('div')
    resultItem.className = 'search-result-item'

    const number = document.createElement('span')
    number.className = 'search-result-number'
    number.textContent = `${index}.`

    const content = document.createElement('div')
    content.className = 'search-result-content'

    const title = document.createElement('h3')
    title.className = 'search-result-title'
    title.innerHTML = `
      <a href="${this.escapeHtml(item.url)}" class="search-result-link">
        ${this.escapeHtml(item.title)}
      </a>
    `
    content.appendChild(title)

    // Add meta/tag section
    const meta = document.createElement('div')
    meta.className = 'search-result-meta'
    const tag = document.createElement('span')
    tag.className = 'search-result-tag'
    tag.textContent = this.formatType(item.type)
    meta.appendChild(tag)
    content.appendChild(meta)

    if (item.introduction) {
      const excerpt = document.createElement('p')
      excerpt.className = 'search-result-excerpt'
      excerpt.textContent = this.truncateText(item.introduction, 150)
      content.appendChild(excerpt)
    }

    resultItem.appendChild(number)
    resultItem.appendChild(content)

    return resultItem
  }

  getExistingResultsCount () {
    // Count existing search results (products, articles, pages)
    const existingResults = document.querySelectorAll('.search-results-items .search-result-item')
    return existingResults.length
  }

  getExistingContentResultsCount () {
    // Count existing search results in content section only
    const existingResults = document.querySelectorAll('#search-content-results .search-result-item')
    return existingResults.length
  }

  updateResultCounts (newTotal) {
    const searchQuery = new URLSearchParams(window.location.search).get('q')

    // Update page title
    const titleElement = document.querySelector('title')
    if (titleElement && searchQuery) {
      // Format will vary by theme, but typically includes count and query
      const baseTitle = titleElement.textContent.split('|')[1] || ''
      titleElement.textContent = `${newTotal} results for "${searchQuery}" | ${baseTitle.trim()}`
    }

    // Update status elements
    const statusElements = document.querySelectorAll('[role="status"]')
    statusElements.forEach(element => {
      // Look for elements that contain result count
      const text = element.textContent
      if (text.includes('result') && searchQuery) {
        // Update with new count - format depends on your translations
        if (newTotal === 0) {
          element.textContent = `No results found for "${searchQuery}"`
        } else if (newTotal === 1) {
          element.textContent = `1 result for "${searchQuery}"`
        } else {
          element.textContent = `${newTotal} results for "${searchQuery}"`
        }
      }
    })

    // Update any count displays in facets or headers
    const countElements = document.querySelectorAll('#ProductCountDesktop, .product-count__text span')
    countElements.forEach(element => {
      const text = element.textContent
      if (text.includes('result') && text.includes(searchQuery)) {
        element.textContent = `${newTotal} results for "${searchQuery}"`
      }
    })
  }

  createPaginationContainer () {
    // Find where to insert pagination
    const resultsContainer = document.querySelector('.template-search__results') ||
                           document.querySelector('.collection') ||
                           document.querySelector('#ProductGridContainer')

    if (resultsContainer) {
      const paginationDiv = document.createElement('div')
      paginationDiv.className = 'pagination'
      resultsContainer.parentElement.insertBefore(paginationDiv, resultsContainer.nextSibling)
      return paginationDiv
    }

    return null
  }

  createResultItem (item, index) {
    const gridItem = document.createElement('li')
    gridItem.className = 'grid__item'

    // Add animation classes if enabled
    if (document.querySelector('[data-cascade]')) {
      gridItem.className += ' scroll-trigger animate--slide-in'
      gridItem.setAttribute('data-cascade', '')
      gridItem.style.setProperty('--animation-delay', `${index * 50}ms`)
    }

    // Create card structure matching theme's article cards
    const cardWrapper = document.createElement('div')
    cardWrapper.className = 'article-card-wrapper card-wrapper'

    // Get theme settings
    const cardStyle = window.theme?.settings?.blog_card_style || 'card'
    const colorScheme = window.theme?.settings?.blog_card_color_scheme || 'background-1'
    const hasImage = item.thumbnail && item.thumbnail.url

    const card = document.createElement('div')
    card.className = `card article-card card--${cardStyle}`
    if (hasImage) {
      card.className += ' card--media'
    } else {
      card.className += ' card--text'
    }
    if (cardStyle === 'card') {
      card.className += ` color-${colorScheme} gradient`
      if (!hasImage) {
        card.className += ' ratio'
      }
    }
    card.style.setProperty('--ratio-percent', '100%')

    // Inner wrapper
    const cardInner = document.createElement('div')
    cardInner.className = 'card__inner'
    if (cardStyle === 'standard') {
      cardInner.className += ` color-${colorScheme} gradient`
    }
    if (hasImage || cardStyle === 'standard') {
      cardInner.className += ' ratio'
    }
    cardInner.style.setProperty('--ratio-percent', '100%')

    // Add image if available
    if (hasImage) {
      const imageWrapper = document.createElement('div')
      imageWrapper.className = 'article-card__image-wrapper card__media'

      const mediaDiv = document.createElement('div')
      mediaDiv.className = 'article-card__image media media--hover-effect'
      // Use aspect ratio from image if available
      if (item.thumbnail.width && item.thumbnail.height) {
        const aspectRatio = item.thumbnail.width / item.thumbnail.height
        mediaDiv.style.paddingBottom = `${(1 / aspectRatio) * 100}%`
      }

      const img = document.createElement('img')
      img.className = 'motion-reduce'
      img.src = item.thumbnail.url
      img.alt = item.thumbnail.altText || item.title
      img.loading = index > 2 ? 'lazy' : 'eager'
      img.width = item.thumbnail.width || 533
      img.height = item.thumbnail.height || 533

      mediaDiv.appendChild(img)
      imageWrapper.appendChild(mediaDiv)
      cardInner.appendChild(imageWrapper)
    }

    // Content section (inside card__inner)
    const cardContentInner = document.createElement('div')
    cardContentInner.className = 'card__content'

    const cardInfoInner = document.createElement('div')
    cardInfoInner.className = 'card__information'

    // Title
    const headingInner = document.createElement('h3')
    headingInner.className = 'card__heading'
    headingInner.innerHTML = `
      <a href="${this.escapeHtml(item.url)}" class="full-unstyled-link">
        ${this.escapeHtml(this.truncateText(item.title, 50))}
      </a>
    `
    cardInfoInner.appendChild(headingInner)

    // Type info (like date/author in article cards)
    const infoDiv = document.createElement('div')
    infoDiv.className = 'article-card__info caption-with-letter-spacing h5'
    infoDiv.innerHTML = `<span>${this.formatType(item.type)}</span>`
    cardInfoInner.appendChild(infoDiv)

    // Introduction/excerpt
    if (item.introduction) {
      const excerpt = document.createElement('p')
      excerpt.className = 'article-card__excerpt rte-width'
      excerpt.textContent = this.truncateText(item.introduction, 100)
      cardInfoInner.appendChild(excerpt)
    }

    cardContentInner.appendChild(cardInfoInner)

    // Badge (show_badge)
    const badgePosition = window.theme?.settings?.badge_position || 'bottom-left'
    const badgeColorScheme = window.theme?.settings?.color_schemes?.[0] || 'background-1'
    const badge = document.createElement('div')
    badge.className = `card__badge ${badgePosition}`
    badge.innerHTML = `
      <span class="badge color-${badgeColorScheme}">
        ${this.formatType(item.type)}
      </span>
    `
    cardContentInner.appendChild(badge)

    cardInner.appendChild(cardContentInner)
    card.appendChild(cardInner)

    // Content section (outside card__inner - for certain card styles)
    const cardContentOuter = document.createElement('div')
    cardContentOuter.className = 'card__content'

    const cardInfoOuter = document.createElement('div')
    cardInfoOuter.className = 'card__information'

    // Duplicate title for outer content
    const headingOuter = document.createElement('h3')
    headingOuter.className = 'card__heading'
    headingOuter.innerHTML = `
      <a href="${this.escapeHtml(item.url)}" class="full-unstyled-link">
        ${this.escapeHtml(this.truncateText(item.title, 50))}
      </a>
    `
    cardInfoOuter.appendChild(headingOuter)

    // Duplicate type info
    const infoDivOuter = document.createElement('div')
    infoDivOuter.className = 'article-card__info caption-with-letter-spacing h5'
    infoDivOuter.innerHTML = `<span>${this.formatType(item.type)}</span>`
    cardInfoOuter.appendChild(infoDivOuter)

    // Duplicate excerpt
    if (item.introduction) {
      const excerptOuter = document.createElement('p')
      excerptOuter.className = 'article-card__excerpt rte-width'
      excerptOuter.textContent = this.truncateText(item.introduction, 100)
      cardInfoOuter.appendChild(excerptOuter)
    }

    cardContentOuter.appendChild(cardInfoOuter)

    // Duplicate badge
    const badgeOuter = document.createElement('div')
    badgeOuter.className = `card__badge ${badgePosition}`
    badgeOuter.innerHTML = `
      <span class="badge color-${badgeColorScheme}">
        ${this.formatType(item.type)}
      </span>
    `
    cardContentOuter.appendChild(badgeOuter)

    card.appendChild(cardContentOuter)
    cardWrapper.appendChild(card)
    gridItem.appendChild(cardWrapper)

    return gridItem
  }

  displayError () {
    const container = document.querySelector(this.resultsContainer)
    if (!container) return

    container.innerHTML = ''

    // Also update the content results section to show error state
    const contentResultsSection = document.querySelector('#search-content-results .search-results-items')
    if (contentResultsSection) {
      // Check if there are any existing content results
      const existingResults = contentResultsSection.querySelectorAll('.search-result-item')
      if (existingResults.length === 0) {
        // Add error message if no existing results
        const errorDiv = document.createElement('div')
        errorDiv.className = 'search-error-message'
        errorDiv.innerHTML = '<p>Unable to search additional content at this time</p>'
        errorDiv.style.padding = '2rem 0'
        errorDiv.style.textAlign = 'center'
        errorDiv.style.color = 'rgba(var(--color-foreground), 0.75)'
        contentResultsSection.appendChild(errorDiv)
      }
    }

    // Update tab counts
    if (window.searchTabManager) {
      window.searchTabManager.updateCounts()
    }
  }

  formatType (type) {
    // Format Sleep Doctor metaobject types for display
    const typeDisplayNames = {
      chronotype_articles: 'Chronotypes',
      cpap_articles: 'CPAP',
      health_articles: 'Health',
      insomnia_articles: 'Insomnia',
      sleep_aids_articles: 'Sleep Aids',
      sleep_apnea_articles: 'Sleep Apnea',
      sleep_doctor_commercial: 'Reviews',
      snoring_articles: 'Snoring'
    }

    return typeDisplayNames[type] || type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  truncateText (text, maxLength) {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}

// Auto-initialize if config is found
document.addEventListener('DOMContentLoaded', function () {
  const configElement = document.querySelector('[data-metaobject-search-config]')
  if (configElement) {
    try {
      const config = JSON.parse(configElement.textContent)
      window.metaobjectSearch = new MetaobjectSearchClient(config)
    } catch (error) {
      console.error('Failed to initialize metaobject search:', error)
    }
  }
})
